import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';
import { authService } from '../services/auth-service.js';

export class SettingsPanel extends LitElement {
  static properties = {
    activeTab: { type: String },
    _toastVisible: { type: Boolean, state: true },
    _toastMessage: { type: String, state: true },
    _twilioStatus: { type: String, state: true },
    _showPasswords: { type: Object, state: true },
    _features: { type: Object, state: true },
    _redaction: { type: Object, state: true },
    _phoneRoutes: { type: Array, state: true },
    _editingRoute: { type: Number, state: true },
    _apiKeys: { type: Array, state: true },
    _webhooks: { type: Array, state: true },
    // Real config state
    _twilioSid: { state: true },
    _twilioToken: { state: true },
    _twilioKey: { state: true },
    _twilioSecret: { state: true },
    _openaiApiKey: { state: true },
    _configLoaded: { state: true },
    _saving: { state: true },
    _testing: { state: true },
    _twilioTestResult: { state: true },
    _toastType: { state: true }
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host {
        display: block;
        animation: fadeIn var(--duration-slow) var(--ease-out) both;
      }

      /* ─── Page Layout ─── */
      .page-header {
        margin-bottom: var(--space-6);
      }

      .page-title {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.04em;
      }

      /* ─── Tab Bar (horizontal) ─── */
      .settings-tabs {
        display: flex;
        gap: var(--space-1);
        border-bottom: 1px solid var(--color-border-primary);
        margin-bottom: var(--space-6);
        overflow-x: auto;
      }

      .settings-tab {
        padding: var(--space-3) var(--space-4);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-tertiary);
        cursor: pointer;
        border: none;
        background: transparent;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;
        transition: all var(--transition-fast);
        white-space: nowrap;
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .settings-tab:hover {
        color: var(--color-text-secondary);
      }

      .settings-tab.active {
        color: var(--color-accent);
        border-bottom-color: var(--color-accent);
        background: var(--color-accent-light);
        border-radius: var(--radius-md) var(--radius-md) 0 0;
      }

      /* ─── Settings Content ─── */
      .settings-content {
        animation: slideUp var(--duration-slow) var(--ease-out) both;
        max-width: 900px;
      }

      .content-header {
        margin-bottom: var(--space-6);
      }

      .content-title {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
        margin-bottom: var(--space-1);
      }

      .content-description {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        line-height: var(--leading-relaxed);
      }

      /* ─── Settings Section ─── */
      .settings-section {
        margin-bottom: var(--space-8);
        background: rgba(255, 255, 255, 0.18);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        position: relative;
        overflow: hidden;
      }

      .section-title-row {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-1);
      }

      .section-icon {
        color: var(--color-primary);
        font-size: var(--text-base);
      }

      /* ─── Form Layout ─── */
      .form-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-4);
      }

      .form-grid.cols-3 {
        grid-template-columns: repeat(3, 1fr);
      }

      .form-grid.full {
        grid-template-columns: 1fr;
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: var(--space-1\.5);
      }

      .form-label {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
      }

      .form-input,
      .form-select,
      .form-textarea {
        padding: var(--space-2) var(--space-3);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        transition: all var(--transition-fast);
        line-height: var(--leading-normal);
        width: 100%;
      }

      .form-input:focus,
      .form-select:focus,
      .form-textarea:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      .form-hint {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      /* ─── Password Input ─── */
      .password-wrap {
        position: relative;
        display: flex;
        align-items: center;
      }

      .password-wrap .form-input {
        padding-right: var(--space-10);
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        letter-spacing: 0.05em;
      }

      .password-toggle {
        position: absolute;
        right: var(--space-2);
        top: 50%;
        transform: translateY(-50%);
        background: transparent;
        border: none;
        color: var(--color-text-tertiary);
        cursor: pointer;
        padding: var(--space-1);
        border-radius: var(--radius-sm);
        transition: color var(--transition-fast);
        font-size: var(--text-sm);
      }

      .password-toggle:hover {
        color: var(--color-text-primary);
      }

      /* ─── Feature Toggle ─── */
      .feature-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      .feature-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-4);
        background: var(--color-bg-tertiary);
        border-radius: var(--radius-xl);
        border: 1px solid var(--color-border-subtle);
        transition: all var(--transition-base);
      }

      .feature-item:hover {
        border-color: var(--color-border-primary);
        transform: scale(1.003);
        box-shadow: var(--shadow-sm);
      }

      .feature-info {
        flex: 1;
        margin-right: var(--space-4);
      }

      .feature-label {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
        margin-bottom: 2px;
      }

      .feature-desc {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        line-height: var(--leading-relaxed);
      }

      /* ─── Toggle Override ─── */
      .toggle {
        transition: background var(--transition-base);
      }

      .toggle::after {
        transition: transform var(--transition-base);
      }

      /* ─── Connection Status ─── */
      .connection-status {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        border-radius: var(--radius-md);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
      }

      .connection-status.success {
        background: var(--color-success-bg);
        color: var(--color-success-text);
      }

      .connection-status.error {
        background: var(--color-error-bg);
        color: var(--color-error-text);
      }

      .connection-status.idle {
        background: var(--color-bg-tertiary);
        color: var(--color-text-tertiary);
      }

      /* ─── Slider ─── */
      .slider-wrap {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .slider-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .slider-value {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-primary);
        background: var(--color-primary-light);
        padding: 2px var(--space-2);
        border-radius: var(--radius-sm);
      }

      input[type="range"] {
        -webkit-appearance: none;
        width: 100%;
        height: 6px;
        background: var(--color-bg-inset);
        border-radius: var(--radius-full);
        outline: none;
      }

      input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 18px;
        height: 18px;
        border-radius: var(--radius-full);
        background: var(--color-primary);
        cursor: pointer;
        box-shadow: var(--shadow-sm);
        transition: all var(--transition-fast);
      }

      input[type="range"]::-webkit-slider-thumb:hover {
        transform: scale(1.15);
        box-shadow: var(--shadow-glow-primary);
      }

      /* ─── Routing Table ─── */
      .table-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }

      .table-wrap {
        background: rgba(255, 255, 255, 0.18);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-lg);
        overflow: hidden;
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
      }

      .route-row-edit .form-input,
      .route-row-edit .form-select {
        padding: var(--space-1\.5) var(--space-2);
        font-size: var(--text-xs);
      }

      .table-actions-cell {
        display: flex;
        gap: var(--space-2);
      }

      .icon-btn {
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-sm);
        border: 1px solid var(--color-border-primary);
        background: var(--color-bg-secondary);
        color: var(--color-text-tertiary);
        cursor: pointer;
        font-size: var(--text-xs);
        transition: all var(--transition-fast);
      }

      .icon-btn:hover {
        background: var(--color-bg-hover);
        color: var(--color-text-primary);
        border-color: var(--color-border-secondary);
      }

      .icon-btn.danger:hover {
        background: var(--color-error-bg);
        color: var(--color-error-text);
        border-color: var(--color-error);
      }

      /* ─── Redaction Toggles ─── */
      .redaction-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--space-3);
      }

      .redaction-item {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3);
        background: var(--color-bg-tertiary);
        border-radius: var(--radius-md);
        border: 1px solid var(--color-border-subtle);
      }

      .redaction-label {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-primary);
        font-weight: var(--font-medium);
      }

      /* ─── API Keys Table ─── */
      .api-key-value {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-secondary);
        background: var(--color-bg-tertiary);
        padding: 2px var(--space-2);
        border-radius: var(--radius-sm);
      }

      /* ─── Webhook Section ─── */
      .webhook-item {
        background: var(--color-bg-tertiary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        padding: var(--space-4);
        margin-bottom: var(--space-3);
      }

      .webhook-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-3);
      }

      .checkbox-group {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-3);
      }

      .checkbox-item {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        cursor: pointer;
      }

      .checkbox-item input[type="checkbox"] {
        width: 16px;
        height: 16px;
        cursor: pointer;
        accent-color: var(--color-primary);
      }

      /* ─── Actions Bar ─── */
      .actions-bar {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-3);
        padding-top: var(--space-6);
        border-top: 1px solid var(--color-border-primary);
        margin-top: var(--space-8);
      }

      /* ─── Toast ─── */
      .toast {
        position: fixed;
        bottom: var(--space-6);
        right: var(--space-6);
        background: var(--color-success);
        color: var(--color-text-inverse);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--radius-lg);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        display: flex;
        align-items: center;
        gap: var(--space-2);
        box-shadow: var(--shadow-xl);
        z-index: var(--z-toast);
        transform: translateY(100px);
        opacity: 0;
        transition: all var(--duration-slow) var(--ease-out);
      }

      .toast.visible {
        transform: translateY(0);
        opacity: 1;
      }

      .toast.error {
        background: var(--color-error);
      }

      .toast.testing {
        background: var(--color-accent);
      }

      /* ─── Responsive ─── */
      @media (max-width: 900px) {
        .form-grid {
          grid-template-columns: 1fr;
        }
        .form-grid.cols-3 {
          grid-template-columns: 1fr;
        }
        .redaction-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      @media (max-width: 600px) {
        .redaction-grid {
          grid-template-columns: 1fr;
        }
      }
    `
  ];

  constructor() {
    super();
    this.activeTab = 'general';
    this._toastVisible = false;
    this._toastMessage = '';
    this._twilioStatus = 'idle'; // idle | success | error
    this._showPasswords = {
      twilioSid: false,
      twilioToken: false,
      twilioKey: false,
      twilioSecret: false,
      openaiKey: false
    };
    this._features = {
      callRecording: true,
      liveTranscription: true,
      sentimentAnalysis: false,
      aiSummaries: true
    };
    this._redaction = {
      creditCards: true,
      ssn: true,
      cvv: true,
      email: false,
      phone: false,
      addresses: false
    };
    this._phoneRoutes = [
      { id: 1, phoneNumber: '+1 (555) 123-4567', parkName: 'Hickory Hills RV Park', agent: 'Park Receptionist', status: 'active' },
      { id: 2, phoneNumber: '+1 (555) 987-6543', parkName: 'Sunset Ridge Campground', agent: 'After Hours Bot', status: 'active' },
      { id: 3, phoneNumber: '+1 (555) 246-8135', parkName: 'Pine Valley Resort', agent: 'Spanish Agent', status: 'inactive' }
    ];
    this._editingRoute = null;
    this._apiKeys = [
      { id: 1, name: 'Production Key', key: 'vk_prod_a3f8...x2k9', created: 'Jan 15, 2026', lastUsed: '2 hours ago' },
      { id: 2, name: 'Staging Key', key: 'vk_stag_b7d2...m4p1', created: 'Dec 3, 2025', lastUsed: '5 days ago' }
    ];
    this._webhooks = [
      { id: 1, url: 'https://api.example.com/webhooks/voice', events: ['call.started', 'call.ended', 'transcript.ready'] }
    ];
    this._openaiTemp = 0.8;
    // Real config values
    this._twilioSid = '';
    this._twilioToken = '';
    this._twilioKey = '';
    this._twilioSecret = '';
    this._openaiApiKey = '';
    this._configLoaded = false;
    this._saving = false;
    this._testing = false;
    this._twilioTestResult = null;
    this._toastType = 'success';
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadConfig();
  }

  async _loadConfig() {
    const token = authService.getToken();
    if (!token) return;

    try {
      const res = await fetch('/voice/php/api/config.php?key=all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.config) {
        this._twilioSid = data.config.twilio_account_sid || '';
        this._twilioToken = data.config.twilio_auth_token || '';
        this._openaiApiKey = data.config.openai_api_key || '';
        this._configLoaded = true;
      }
    } catch (err) {
      console.error('[Settings] Failed to load config:', err);
    }
  }

  _setTab(tab) {
    this.activeTab = tab;
  }

  _togglePassword(field) {
    this._showPasswords = { ...this._showPasswords, [field]: !this._showPasswords[field] };
  }

  _toggleFeature(key) {
    this._features = { ...this._features, [key]: !this._features[key] };
  }

  _toggleRedaction(key) {
    this._redaction = { ...this._redaction, [key]: !this._redaction[key] };
  }

  async _testTwilioConnection() {
    if (this._testing) return;
    if (!this._twilioSid || !this._twilioToken) {
      this._showToast('Enter Account SID and Auth Token first', 'error');
      return;
    }

    this._testing = true;
    this._twilioStatus = 'idle';
    this._twilioTestResult = null;

    try {
      const token = authService.getToken();
      const res = await fetch('/voice/php/api/twilio-test.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          account_sid: this._twilioSid,
          auth_token: this._twilioToken
        })
      });

      const data = await res.json();

      if (data.success) {
        this._twilioStatus = 'success';
        this._twilioTestResult = data.account;
        this._showToast(`Connected: ${data.account?.friendly_name || 'Twilio account verified'}`);
      } else {
        this._twilioStatus = 'error';
        this._showToast(data.error || 'Connection failed', 'error');
      }
    } catch (err) {
      this._twilioStatus = 'error';
      this._showToast('Network error testing connection', 'error');
    } finally {
      this._testing = false;
      setTimeout(() => { this._twilioStatus = 'idle'; this._twilioTestResult = null; }, 10000);
    }
  }

  _showToast(message, type = 'success') {
    this._toastMessage = message;
    this._toastType = type;
    this._toastVisible = true;
    setTimeout(() => { this._toastVisible = false; }, 4000);
  }

  async _handleSave() {
    if (this._saving) return;
    this._saving = true;

    // Collect data based on active tab
    let payload = {};

    if (this.activeTab === 'twilio') {
      payload = {
        twilio_account_sid: this._twilioSid,
        twilio_auth_token: this._twilioToken
      };
      // Only send optional fields if filled
      if (this._twilioKey) payload.twilio_api_key = this._twilioKey;
      if (this._twilioSecret) payload.twilio_api_secret = this._twilioSecret;
    } else if (this.activeTab === 'openai') {
      payload = {
        openai_api_key: this._openaiApiKey
      };
    }

    if (Object.keys(payload).length === 0) {
      this._showToast('Nothing to save', 'error');
      this._saving = false;
      return;
    }

    try {
      const token = authService.getToken();
      const res = await fetch('/voice/php/api/config-save.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (data.success) {
        this._showToast(`${data.message || 'Settings saved'}`);
      } else {
        this._showToast(data.error || 'Save failed', 'error');
      }
    } catch (err) {
      this._showToast('Network error saving settings', 'error');
    } finally {
      this._saving = false;
    }
  }

  _setEditingRoute(id) {
    this._editingRoute = this._editingRoute === id ? null : id;
  }

  _deleteRoute(id) {
    this._phoneRoutes = this._phoneRoutes.filter(r => r.id !== id);
  }

  _addRoute() {
    const newId = Math.max(...this._phoneRoutes.map(r => r.id), 0) + 1;
    this._phoneRoutes = [...this._phoneRoutes, {
      id: newId,
      phoneNumber: '+1 (555) 000-0000',
      parkName: 'New Park',
      agent: 'Park Receptionist',
      status: 'inactive'
    }];
    this._editingRoute = newId;
  }

  _updateTemp(e) {
    this._openaiTemp = parseFloat(e.target.value);
  }

  render() {
    const tabs = [
      { key: 'general', label: 'General', icon: 'fa-gear' },
      { key: 'twilio', label: 'Twilio', icon: 'fa-phone-office' },
      { key: 'openai', label: 'OpenAI', icon: 'fa-microchip-ai' },
      { key: 'routing', label: 'Phone Routing', icon: 'fa-route' },
      { key: 'security', label: 'Security', icon: 'fa-shield-halved' },
      { key: 'api', label: 'API & Webhooks', icon: 'fa-webhook' }
    ];

    return html`
      ${iconStyles}
      <div class="page-header">
        <h1 class="page-title">Settings</h1>
      </div>

      <div class="settings-tabs">
        ${tabs.map(tab => html`
          <button
            class="settings-tab ${this.activeTab === tab.key ? 'active' : ''}"
            @click=${() => this._setTab(tab.key)}
          >
            <i class="fa-sharp fa-light ${tab.icon}"></i>
            ${tab.label}
          </button>
        `)}
      </div>

      <div class="settings-content">
        ${this.activeTab === 'general' ? this._renderGeneral() : ''}
        ${this.activeTab === 'twilio' ? this._renderTwilio() : ''}
        ${this.activeTab === 'openai' ? this._renderOpenAI() : ''}
        ${this.activeTab === 'routing' ? this._renderRouting() : ''}
        ${this.activeTab === 'security' ? this._renderSecurity() : ''}
        ${this.activeTab === 'api' ? this._renderAPI() : ''}
      </div>

      <div class="toast ${this._toastVisible ? 'visible' : ''} ${this._toastType || 'success'}">
        <i class="fa-sharp fa-light ${this._toastType === 'error' ? 'fa-circle-xmark' : 'fa-check'}"></i>
        ${this._toastMessage}
      </div>
    `;
  }

  /* ───────────────────────────────────
     General Tab
  ─────────────────────────────────── */
  _renderGeneral() {
    return html`
      <div class="content-header">
        <h2 class="content-title">General Settings</h2>
        <p class="content-description">Configure system-wide preferences including timezone, formatting, and feature toggles.</p>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-globe section-icon"></i>
          <span class="section-title">Regional Preferences</span>
        </div>
        <p class="section-description">Set your timezone, date format, and default language.</p>

        <div class="form-grid cols-3">
          <div class="form-group">
            <label class="form-label">Timezone</label>
            <select class="form-select">
              <option value="America/New_York" selected>Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="America/Anchorage">Alaska Time (AKT)</option>
              <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Date Format</label>
            <select class="form-select">
              <option value="MM/DD/YYYY" selected>MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Language</label>
            <select class="form-select">
              <option value="en-US" selected>English (US)</option>
              <option value="en-GB">English (UK)</option>
              <option value="es-ES">Spanish</option>
              <option value="fr-FR">French</option>
            </select>
          </div>
        </div>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-toggle-on section-icon"></i>
          <span class="section-title">Feature Toggles</span>
        </div>
        <p class="section-description">Enable or disable system-wide features.</p>

        <div class="feature-list">
          <div class="feature-item">
            <div class="feature-info">
              <div class="feature-label">
                <i class="fa-sharp fa-light fa-circle-dot" style="margin-right:var(--space-2);color:var(--color-error);font-size:var(--text-xs);"></i>
                Call Recording
              </div>
              <div class="feature-desc">Automatically record all incoming and outgoing calls for quality assurance and compliance.</div>
            </div>
            <div
              class="toggle ${this._features.callRecording ? 'active' : ''}"
              @click=${() => this._toggleFeature('callRecording')}
            ></div>
          </div>

          <div class="feature-item">
            <div class="feature-info">
              <div class="feature-label">
                <i class="fa-sharp fa-light fa-closed-captioning" style="margin-right:var(--space-2);color:var(--color-accent);font-size:var(--text-xs);"></i>
                Live Transcription
              </div>
              <div class="feature-desc">Generate real-time transcripts during active calls for monitoring and review.</div>
            </div>
            <div
              class="toggle ${this._features.liveTranscription ? 'active' : ''}"
              @click=${() => this._toggleFeature('liveTranscription')}
            ></div>
          </div>

          <div class="feature-item">
            <div class="feature-info">
              <div class="feature-label">
                <i class="fa-sharp fa-light fa-face-smile" style="margin-right:var(--space-2);color:var(--color-warning);font-size:var(--text-xs);"></i>
                Sentiment Analysis
              </div>
              <div class="feature-desc">Analyze caller sentiment in real-time to detect frustration, satisfaction, and urgency.</div>
            </div>
            <div
              class="toggle ${this._features.sentimentAnalysis ? 'active' : ''}"
              @click=${() => this._toggleFeature('sentimentAnalysis')}
            ></div>
          </div>

          <div class="feature-item">
            <div class="feature-info">
              <div class="feature-label">
                <i class="fa-sharp fa-light fa-sparkles" style="margin-right:var(--space-2);color:var(--color-info);font-size:var(--text-xs);"></i>
                AI Summaries
              </div>
              <div class="feature-desc">Generate AI-powered summaries after each call with key topics, action items, and outcomes.</div>
            </div>
            <div
              class="toggle ${this._features.aiSummaries ? 'active' : ''}"
              @click=${() => this._toggleFeature('aiSummaries')}
            ></div>
          </div>
        </div>
      </div>

      ${this._renderActionsBar()}
    `;
  }

  /* ───────────────────────────────────
     Twilio Tab
  ─────────────────────────────────── */
  _renderTwilio() {
    return html`
      <div class="content-header">
        <h2 class="content-title">Twilio Configuration</h2>
        <p class="content-description">Connect your Twilio account to enable phone call functionality. Find your credentials in the Twilio Console.</p>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-key section-icon"></i>
          <span class="section-title">Account Credentials</span>
        </div>
        <p class="section-description">Enter your Twilio Account SID and Auth Token from the Twilio Console Dashboard.</p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Account SID</label>
            <div class="password-wrap">
              <input
                type="${this._showPasswords.twilioSid ? 'text' : 'password'}"
                class="form-input"
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                .value=${this._twilioSid}
                @input=${e => this._twilioSid = e.target.value}
              />
              <button class="password-toggle" @click=${() => this._togglePassword('twilioSid')}>
                <i class="fa-sharp fa-light ${this._showPasswords.twilioSid ? 'fa-eye-slash' : 'fa-eye'}"></i>
              </button>
            </div>
            <div class="form-hint">Your Twilio Account SID (starts with AC).</div>
          </div>

          <div class="form-group">
            <label class="form-label">Auth Token</label>
            <div class="password-wrap">
              <input
                type="${this._showPasswords.twilioToken ? 'text' : 'password'}"
                class="form-input"
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                .value=${this._twilioToken}
                @input=${e => this._twilioToken = e.target.value}
              />
              <button class="password-toggle" @click=${() => this._togglePassword('twilioToken')}>
                <i class="fa-sharp fa-light ${this._showPasswords.twilioToken ? 'fa-eye-slash' : 'fa-eye'}"></i>
              </button>
            </div>
            <div class="form-hint">Keep this secret. Never share your Auth Token.</div>
          </div>

          <div class="form-group">
            <label class="form-label">API Key</label>
            <div class="password-wrap">
              <input
                type="${this._showPasswords.twilioKey ? 'text' : 'password'}"
                class="form-input"
                placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                .value=${this._twilioKey}
                @input=${e => this._twilioKey = e.target.value}
              />
              <button class="password-toggle" @click=${() => this._togglePassword('twilioKey')}>
                <i class="fa-sharp fa-light ${this._showPasswords.twilioKey ? 'fa-eye-slash' : 'fa-eye'}"></i>
              </button>
            </div>
            <div class="form-hint">Optional API Key for enhanced security (starts with SK).</div>
          </div>

          <div class="form-group">
            <label class="form-label">API Secret</label>
            <div class="password-wrap">
              <input
                type="${this._showPasswords.twilioSecret ? 'text' : 'password'}"
                class="form-input"
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                .value=${this._twilioSecret}
                @input=${e => this._twilioSecret = e.target.value}
              />
              <button class="password-toggle" @click=${() => this._togglePassword('twilioSecret')}>
                <i class="fa-sharp fa-light ${this._showPasswords.twilioSecret ? 'fa-eye-slash' : 'fa-eye'}"></i>
              </button>
            </div>
            <div class="form-hint">API Secret corresponding to the API Key above.</div>
          </div>
        </div>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-plug section-icon"></i>
          <span class="section-title">Connection Test</span>
        </div>
        <p class="section-description">Verify that your credentials are valid and the connection to Twilio is working.</p>

        <div style="display:flex;align-items:center;gap:var(--space-4);margin-top:var(--space-3);">
          <button class="btn btn-secondary" @click=${this._testTwilioConnection} ?disabled=${this._testing}>
            ${this._testing
              ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i> Testing...`
              : html`<i class="fa-sharp fa-light fa-plug"></i> Test Connection`
            }
          </button>
          <span class="connection-status ${this._twilioStatus}">
            ${this._twilioStatus === 'success' ? html`
              <i class="fa-sharp fa-light fa-check-circle"></i> Connected${this._twilioTestResult?.friendly_name ? ` — ${this._twilioTestResult.friendly_name}` : ''}
            ` : ''}
            ${this._twilioStatus === 'error' ? html`
              <i class="fa-sharp fa-light fa-circle-xmark"></i> Connection failed
            ` : ''}
            ${this._twilioStatus === 'idle' ? html`
              <i class="fa-sharp fa-light fa-circle-minus"></i> Not tested yet
            ` : ''}
          </span>
        </div>
      </div>

      ${this._renderActionsBar()}
    `;
  }

  /* ───────────────────────────────────
     OpenAI Tab
  ─────────────────────────────────── */
  _renderOpenAI() {
    return html`
      <div class="content-header">
        <h2 class="content-title">OpenAI Configuration</h2>
        <p class="content-description">Configure the OpenAI Realtime API for AI-powered voice conversations. Usage is billed per minute of audio.</p>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-key section-icon"></i>
          <span class="section-title">API Key</span>
        </div>

        <div class="form-grid full">
          <div class="form-group">
            <label class="form-label">OpenAI API Key</label>
            <div class="password-wrap">
              <input
                type="${this._showPasswords.openaiKey ? 'text' : 'password'}"
                class="form-input"
                placeholder="sk-proj-xxxxxxxxxxxxxxxxxxxxxxxx"
                .value=${this._openaiApiKey}
                @input=${e => this._openaiApiKey = e.target.value}
              />
              <button class="password-toggle" @click=${() => this._togglePassword('openaiKey')}>
                <i class="fa-sharp fa-light ${this._showPasswords.openaiKey ? 'fa-eye-slash' : 'fa-eye'}"></i>
              </button>
            </div>
            <div class="form-hint">Your OpenAI API key (starts with sk-proj-). Keep this secret.</div>
          </div>
        </div>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-sliders section-icon"></i>
          <span class="section-title">Model Settings</span>
        </div>
        <p class="section-description">Choose the model and fine-tune generation parameters.</p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Model</label>
            <select class="form-select">
              <option value="gpt-4o-realtime-preview" selected>gpt-4o-realtime-preview</option>
              <option value="gpt-4o-mini">gpt-4o-mini</option>
            </select>
            <div class="form-hint">Realtime model for voice conversations.</div>
          </div>

          <div class="form-group">
            <label class="form-label">Max Tokens</label>
            <input type="number" class="form-input" value="4096" min="256" max="16384" step="256" />
            <div class="form-hint">Maximum tokens per response (256 - 16384).</div>
          </div>
        </div>

        <div class="form-grid full" style="margin-top:var(--space-4);">
          <div class="form-group">
            <div class="slider-wrap">
              <div class="slider-header">
                <label class="form-label">Temperature</label>
                <span class="slider-value">${this._openaiTemp.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                .value=${String(this._openaiTemp)}
                @input=${this._updateTemp}
              />
              <div class="form-hint">Controls randomness. Lower values (0.0) are more focused and deterministic, higher values (1.0) are more creative.</div>
            </div>
          </div>
        </div>
      </div>

      ${this._renderActionsBar()}
    `;
  }

  /* ───────────────────────────────────
     Phone Routing Tab
  ─────────────────────────────────── */
  _renderRouting() {
    return html`
      <div class="content-header">
        <h2 class="content-title">Phone Routing</h2>
        <p class="content-description">Map phone numbers to parks and assign AI agents. Each number routes calls to the configured agent.</p>
      </div>

      <div class="settings-section">
        <div class="table-header">
          <div class="section-title-row">
            <i class="fa-sharp-duotone fa-solid fa-phone-arrow-right section-icon"></i>
            <span class="section-title">Number Assignments</span>
          </div>
          <button class="btn btn-primary btn-sm" @click=${this._addRoute}>
            <i class="fa-sharp fa-light fa-plus"></i>
            Add Route
          </button>
        </div>

        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Phone Number</th>
                <th>Park Name</th>
                <th>Agent</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              ${this._phoneRoutes.map(route => html`
                <tr>
                  <td>
                    ${this._editingRoute === route.id
                      ? html`<input type="text" class="form-input" .value=${route.phoneNumber} style="padding:var(--space-1) var(--space-2);font-family:var(--font-mono);font-size:var(--text-xs);width:160px;" />`
                      : html`<span style="font-family:var(--font-mono);color:var(--color-text-primary);">${route.phoneNumber}</span>`
                    }
                  </td>
                  <td>
                    ${this._editingRoute === route.id
                      ? html`<input type="text" class="form-input" .value=${route.parkName} style="padding:var(--space-1) var(--space-2);font-size:var(--text-xs);width:180px;" />`
                      : html`<span style="font-weight:var(--font-medium);color:var(--color-text-primary);">${route.parkName}</span>`
                    }
                  </td>
                  <td>
                    ${this._editingRoute === route.id
                      ? html`
                        <select class="form-select" style="padding:var(--space-1) var(--space-2);font-size:var(--text-xs);width:160px;">
                          <option ?selected=${route.agent === 'Park Receptionist'}>Park Receptionist</option>
                          <option ?selected=${route.agent === 'After Hours Bot'}>After Hours Bot</option>
                          <option ?selected=${route.agent === 'Spanish Agent'}>Spanish Agent</option>
                          <option ?selected=${route.agent === 'Test Agent'}>Test Agent</option>
                        </select>`
                      : html`<span>${route.agent}</span>`
                    }
                  </td>
                  <td>
                    <span class="badge ${route.status === 'active' ? 'badge-success' : 'badge-neutral'}">
                      <span class="status-dot ${route.status === 'active' ? 'active' : 'inactive'}"></span>
                      ${route.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div class="table-actions-cell">
                      <button
                        class="icon-btn"
                        title="${this._editingRoute === route.id ? 'Save' : 'Edit'}"
                        @click=${() => this._setEditingRoute(route.id)}
                      >
                        <i class="fa-sharp fa-light ${this._editingRoute === route.id ? 'fa-check' : 'fa-pen'}"></i>
                      </button>
                      <button class="icon-btn danger" title="Delete" @click=${() => this._deleteRoute(route.id)}>
                        <i class="fa-sharp fa-light fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      </div>

      ${this._renderActionsBar()}
    `;
  }

  /* ───────────────────────────────────
     Security Tab
  ─────────────────────────────────── */
  _renderSecurity() {
    return html`
      <div class="content-header">
        <h2 class="content-title">Security & Privacy</h2>
        <p class="content-description">Configure transcript redaction rules and data retention policies to comply with PCI-DSS and privacy regulations.</p>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-shield-check section-icon"></i>
          <span class="section-title">Transcript Redaction</span>
        </div>
        <p class="section-description">Automatically detect and redact sensitive information from call transcripts.</p>

        <div class="redaction-grid">
          <div class="redaction-item">
            <div
              class="toggle ${this._redaction.creditCards ? 'active' : ''}"
              @click=${() => this._toggleRedaction('creditCards')}
            ></div>
            <span class="redaction-label">Credit Cards</span>
          </div>
          <div class="redaction-item">
            <div
              class="toggle ${this._redaction.ssn ? 'active' : ''}"
              @click=${() => this._toggleRedaction('ssn')}
            ></div>
            <span class="redaction-label">SSN</span>
          </div>
          <div class="redaction-item">
            <div
              class="toggle ${this._redaction.cvv ? 'active' : ''}"
              @click=${() => this._toggleRedaction('cvv')}
            ></div>
            <span class="redaction-label">CVV</span>
          </div>
          <div class="redaction-item">
            <div
              class="toggle ${this._redaction.email ? 'active' : ''}"
              @click=${() => this._toggleRedaction('email')}
            ></div>
            <span class="redaction-label">Email</span>
          </div>
          <div class="redaction-item">
            <div
              class="toggle ${this._redaction.phone ? 'active' : ''}"
              @click=${() => this._toggleRedaction('phone')}
            ></div>
            <span class="redaction-label">Phone Numbers</span>
          </div>
          <div class="redaction-item">
            <div
              class="toggle ${this._redaction.addresses ? 'active' : ''}"
              @click=${() => this._toggleRedaction('addresses')}
            ></div>
            <span class="redaction-label">Addresses</span>
          </div>
        </div>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-clock-rotate-left section-icon"></i>
          <span class="section-title">Data Retention</span>
        </div>
        <p class="section-description">Set how long data is kept before automatic deletion.</p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Call Recordings Retention</label>
            <select class="form-select">
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90" selected>90 days</option>
              <option value="180">180 days</option>
            </select>
            <div class="form-hint">How long to keep call audio recordings.</div>
          </div>
          <div class="form-group">
            <label class="form-label">Transcripts Retention</label>
            <select class="form-select">
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
              <option value="180" selected>180 days</option>
            </select>
            <div class="form-hint">How long to keep call transcripts.</div>
          </div>
        </div>
      </div>

      ${this._renderActionsBar()}
    `;
  }

  /* ───────────────────────────────────
     API & Webhooks Tab
  ─────────────────────────────────── */
  _renderAPI() {
    return html`
      <div class="content-header">
        <h2 class="content-title">API & Webhooks</h2>
        <p class="content-description">Manage API keys for external integrations and configure webhook endpoints for event notifications.</p>
      </div>

      <!-- API Keys Section -->
      <div class="settings-section">
        <div class="table-header">
          <div>
            <div class="section-title-row">
              <i class="fa-sharp-duotone fa-solid fa-key section-icon"></i>
              <span class="section-title">API Keys</span>
            </div>
            <p class="section-description" style="margin-top:var(--space-1);">Use API keys to authenticate requests to the Voice AI API.</p>
          </div>
          <button class="btn btn-primary btn-sm">
            <i class="fa-sharp fa-light fa-plus"></i>
            Generate New Key
          </button>
        </div>

        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Created</th>
                <th>Last Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              ${this._apiKeys.map(key => html`
                <tr>
                  <td style="font-weight:var(--font-medium);color:var(--color-text-primary);">${key.name}</td>
                  <td><span class="api-key-value">${key.key}</span></td>
                  <td>${key.created}</td>
                  <td>${key.lastUsed}</td>
                  <td>
                    <div class="table-actions-cell">
                      <button class="icon-btn" title="Copy">
                        <i class="fa-sharp fa-light fa-copy"></i>
                      </button>
                      <button class="icon-btn" title="Regenerate">
                        <i class="fa-sharp fa-light fa-rotate"></i>
                      </button>
                      <button class="icon-btn danger" title="Revoke">
                        <i class="fa-sharp fa-light fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      </div>

      <!-- Webhooks Section -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-webhook section-icon"></i>
          <span class="section-title">Webhook Endpoints</span>
        </div>
        <p class="section-description">Configure URLs to receive real-time event notifications.</p>

        ${this._webhooks.map(wh => html`
          <div class="webhook-item">
            <div class="webhook-header">
              <div class="form-group" style="flex:1;margin-right:var(--space-3);">
                <label class="form-label">Endpoint URL</label>
                <input type="url" class="form-input" .value=${wh.url} placeholder="https://your-server.com/webhook" />
              </div>
              <button class="btn btn-secondary btn-sm" style="align-self:flex-end;">
                <i class="fa-sharp fa-light fa-paper-plane"></i>
                Test
              </button>
            </div>
            <div>
              <label class="form-label" style="margin-bottom:var(--space-2);display:block;">Event Types</label>
              <div class="checkbox-group">
                ${[
                  { value: 'call.started', label: 'Call Started' },
                  { value: 'call.ended', label: 'Call Ended' },
                  { value: 'call.failed', label: 'Call Failed' },
                  { value: 'transcript.ready', label: 'Transcript Ready' },
                  { value: 'summary.ready', label: 'Summary Ready' },
                  { value: 'agent.error', label: 'Agent Error' }
                ].map(evt => html`
                  <label class="checkbox-item">
                    <input type="checkbox" ?checked=${wh.events.includes(evt.value)} />
                    ${evt.label}
                  </label>
                `)}
              </div>
            </div>
          </div>
        `)}

        <button class="btn btn-secondary btn-sm" style="margin-top:var(--space-2);">
          <i class="fa-sharp fa-light fa-plus"></i>
          Add Webhook Endpoint
        </button>
      </div>

      ${this._renderActionsBar()}
    `;
  }

  /* ─── Shared Actions Bar ─── */
  _renderActionsBar() {
    return html`
      <div class="actions-bar">
        <button class="btn btn-primary" @click=${this._handleSave} ?disabled=${this._saving}>
          ${this._saving
            ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i> Saving...`
            : html`<i class="fa-sharp fa-light fa-floppy-disk"></i> Save Changes`
          }
        </button>
      </div>
    `;
  }
}

customElements.define('settings-panel', SettingsPanel);
